import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Search, FolderGit2, Github, Clock, ArrowRight, X, Loader2, Box, Globe, Server, Terminal,
  AlertTriangle, RefreshCw, MoreHorizontal, Pencil, Trash2,
} from "lucide-react";
import { CreateProjectModal } from "@/components/CreateProjectModal";
import OnboardingWizard from "@/components/OnboardingWizard";

const TYPE_ICON = { web: Globe, node: Server, python: Terminal, other: Box };

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [menuFor, setMenuFor] = useState(null); // project id whose card menu is open
  const [renameTarget, setRenameTarget] = useState(null); // project being renamed
  const [deleteTarget, setDeleteTarget] = useState(null); // project pending delete confirm
  const [onboardingDone, setOnboardingDone] = useState(() => !!localStorage.getItem("cadence_onboarding_done"));

  async function load() {
    setLoading(true);
    setLoadError(false);
    try {
      const { data } = await api.get("/projects", { params: { q }, skipErrorToast: true });
      setProjects(data.projects);
    } catch (e) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  async function deleteProject() {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!target) return;
    try {
      await api.delete(`/projects/${target.id}`, { skipErrorToast: true });
      toast.success(`Deleted ${target.name}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  const recent = projects.slice(0, 1)[0];

  // First-run: an authed user with zero projects who hasn't dismissed/finished the
  // wizard sees the guided setup instead of the bare empty state.
  const showOnboarding = !loading && !loadError && projects.length === 0 && !onboardingDone;

  function finishOnboarding(project) {
    localStorage.setItem("cadence_onboarding_done", "1");
    setOnboardingDone(true);
    if (project) navigate(`/workspace/${project.id}`);
    else load();
  }

  return (
    <div className="flex-1 overflow-y-auto cadence-scroll">
      <div className="max-w-6xl mx-auto px-8 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="label-caps mb-2">Workspace</p>
            <h1 className="font-heading font-800 text-4xl tracking-tight text-gray-900">
              Good to see you, {(user?.name || "dev").split(" ")[0]}.
            </h1>
            <p className="text-gray-500 mt-2 text-[15px]">Pick up a task or start something new.</p>
          </div>
          {!showOnboarding && (
            <button data-testid="project-create-button" onClick={() => setShowCreate(true)}
              className="bg-black text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-900 transition-colors inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> New project
            </button>
          )}
        </div>

        {/* hero actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {!showOnboarding && (
            <button data-testid="hero-create" onClick={() => setShowCreate(true)}
              className="text-left bg-[#0A0A0A] text-white rounded-xl p-5 hover:-translate-y-[2px] transition-transform">
              <Plus className="w-5 h-5 mb-8" />
              <div className="font-heading font-700 text-lg">Create project</div>
              <div className="text-gray-400 text-sm mt-1">Start a fresh workspace from scratch.</div>
            </button>
          )}
          {!showOnboarding && (
            <button data-testid="hero-import" onClick={() => setShowCreate(true)}
              className="text-left bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md hover:-translate-y-[2px] transition-all">
              <Github className="w-5 h-5 mb-8 text-gray-700" />
              <div className="font-heading font-700 text-lg text-gray-900">Import GitHub repo</div>
              <div className="text-gray-500 text-sm mt-1">Bring an existing repo into Cadence.</div>
            </button>
          )}
          <button data-testid="hero-resume" disabled={!recent} onClick={() => recent && navigate(`/workspace/${recent.id}`)}
            className="text-left bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md hover:-translate-y-[2px] transition-all disabled:opacity-50">
            <Clock className="w-5 h-5 mb-8 text-gray-700" />
            <div className="font-heading font-700 text-lg text-gray-900">Resume last session</div>
            <div className="text-gray-500 text-sm mt-1 truncate">{recent ? recent.name : "No recent projects yet."}</div>
          </button>
        </div>

        {/* search + list */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-700 text-lg text-gray-900">All projects</h2>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input data-testid="project-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search projects" className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-blue-500 w-64" />
          </div>
        </div>

        {loading ? (
          <div className="py-20 grid place-items-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : loadError ? (
          <div className="py-16 grid place-items-center" data-testid="projects-load-error">
            <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center max-w-sm w-full shadow-sm">
              <AlertTriangle className="w-8 h-8 mx-auto text-amber-500 mb-3" />
              <p className="font-heading font-700 text-gray-900">Couldn't load your projects</p>
              <p className="text-sm text-gray-500 mt-1">Check your connection and try again.</p>
              <button data-testid="projects-retry" onClick={load}
                className="mt-5 bg-black text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-900 inline-flex items-center gap-2">
                <RefreshCw className="w-4 h-4" /> Retry
              </button>
            </div>
          </div>
        ) : projects.length === 0 ? (
          showOnboarding ? (
            <OnboardingWizard onClose={() => finishOnboarding(null)} onComplete={(p) => finishOnboarding(p)} />
          ) : (
            <div className="py-16 text-center border border-dashed border-gray-300 rounded-xl">
              <FolderGit2 className="w-8 h-8 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 text-sm">No projects yet. Create your first one.</p>
            </div>
          )
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => {
              const Icon = TYPE_ICON[p.project_type] || Box;
              return (
                <div key={p.id} data-testid="project-card" role="button" tabIndex={0} onClick={() => navigate(`/workspace/${p.id}`)}
                  onKeyDown={(e) => { if (e.key === "Enter") navigate(`/workspace/${p.id}`); }}
                  className="group relative text-left bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md hover:-translate-y-[1px] transition-all flex flex-col cursor-pointer">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: `${p.color}1a`, color: p.color }}>
                      <Icon className="w-4.5 h-4.5" strokeWidth={1.8} />
                    </div>
                    <div className="flex items-center gap-1">
                      <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-900 transition-colors" />
                      <button data-testid="project-card-menu" title="Project actions"
                        onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === p.id ? null : p.id); }}
                        className="text-gray-300 hover:text-gray-900 hover:bg-gray-100 rounded-md p-1 transition-colors">
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {menuFor === p.id && (
                    <>
                      <div className="fixed inset-0 z-30" onClick={(e) => { e.stopPropagation(); setMenuFor(null); }} />
                      <div className="absolute top-12 right-3 bg-white border border-gray-200 rounded-xl shadow-lg py-1.5 w-44 z-40" onClick={(e) => e.stopPropagation()}>
                        <button data-testid="project-rename" onClick={() => { setMenuFor(null); setRenameTarget(p); }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2">
                          <Pencil className="w-4 h-4" /> Rename
                        </button>
                        <button data-testid="project-delete" onClick={() => { setMenuFor(null); setDeleteTarget(p); }}
                          className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 inline-flex items-center gap-2">
                          <Trash2 className="w-4 h-4" /> Delete
                        </button>
                      </div>
                    </>
                  )}
                  <div className="font-heading font-700 text-[15px] text-gray-900 truncate">{p.name}</div>
                  <div className="text-sm text-gray-500 mt-1 line-clamp-2 flex-1">{p.description || "No description."}</div>
                  <div className="mt-4 flex items-center gap-2">
                    <span className="text-[11px] px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 border border-gray-200">{p.project_type}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {showCreate && !showOnboarding && !loading && <CreateProjectModal onClose={() => setShowCreate(false)} onCreated={(p) => { setShowCreate(false); navigate(`/workspace/${p.id}`); }} />}
      {renameTarget && <RenameProjectModal project={renameTarget} onClose={() => setRenameTarget(null)}
        onRenamed={() => { setRenameTarget(null); load(); }} />}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the project and its tasks. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="project-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="project-delete-confirm" onClick={deleteProject}
              className="bg-red-600 text-white hover:bg-red-700">
              Delete project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RenameProjectModal({ project, onClose, onRenamed }) {
  const [name, setName] = useState(project.name);
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}`, { name: name.trim() }, { skipErrorToast: true });
      toast.success("Project renamed");
      onRenamed();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-700 text-lg">Rename project</h3>
          <button data-testid="modal-close" onClick={onClose} className="text-gray-400 hover:text-gray-900 p-1"><X className="w-4 h-4" /></button>
        </div>
        <input data-testid="rename-project-name" autoFocus value={name} onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); }} placeholder="Project name"
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500" />
        <button data-testid="rename-project-submit" disabled={busy || !name.trim()} onClick={save}
          className="mt-4 w-full bg-black text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-900 disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </button>
      </div>
    </div>
  );
}
